import { Link } from '@tanstack/react-router';
import { ExternalLink, ShieldAlert, Smartphone } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/ui/empty-state';
import { useAuth } from '@/lib/auth';
import type { Session } from '@/lib/schemas';

type Role = Session['user']['role'];

/* ---------------------------------------------------------------------------
   מי רואה מה.

   ההרשאה נאכפת בשרת על כל בקשה; כאן זו רק חוויה. בלי זה, מי שמגיע לכתובת
   ישירה של מסך שאינו שלו ראה מסך שכל בקשה בו נכשלת ב-403 — שגיאה במקום
   הסבר. שני מקרים:

   • טכנאי שנכנס לאתר — מועבר לאפליקציית השטח. זה הכלי שלו, והאתר לא בנוי
     למסך של טלפון בשמש.
   • מנהל שמגיע למסך של בעלים — מקבל הסבר וכפתור חזרה, לא שגיאה.
   --------------------------------------------------------------------------- */

/** אפליקציית הטכנאי יושבת תחת /field/ באותו דומיין (ראו docs/06-deployment.md). */
const FIELD_APP_URL = '/field/';

export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { t } = useTranslation();
  const { session, status } = useAuth();

  // טכנאי מגיע לאפליקציה שלו, בלי שיצטרך לדעת שהיא קיימת.
  useEffect(() => {
    if (session?.user.role === 'FIELD') window.location.replace(FIELD_APP_URL);
  }, [session?.user.role]);

  if (status === 'loading' || !session) return null;

  if (session.user.role === 'FIELD') {
    return (
      <EmptyState
        icon={Smartphone}
        title={t('access.fieldApp.title')}
        description={t('access.fieldApp.description')}
        action={
          // קישור אמיתי ולא Button asChild: ה-Slot של Radix מקבל ילד אחד,
          // ולכפתור שלנו יש גם ספינר — צירוף שמפיל את המסך בזמן ריצה.
          <a
            href={FIELD_APP_URL}
            className="inline-flex h-11 items-center gap-2 rounded-(--radius-md) bg-accent px-4 text-sm font-medium text-fg-on-accent shadow-xs hover:bg-accent-hover"
          >
            {t('access.fieldApp.open')}
            <ExternalLink className="size-4" aria-hidden />
          </a>
        }
      />
    );
  }

  if (!roles.includes(session.user.role)) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title={t('access.ownerOnly.title')}
        description={t('access.ownerOnly.description')}
        action={
          <Link
            to="/"
            className="inline-flex h-11 items-center rounded-(--radius-md) border border-border bg-surface-raised px-4 text-sm font-medium text-fg shadow-xs hover:bg-surface-hover"
          >
            {t('access.ownerOnly.back')}
          </Link>
        }
      />
    );
  }

  return <>{children}</>;
}
